import { useSupabase } from '@packages/supabase'
import { Alert, Badge, Grid, Group, Loader, Stack, Text } from '@mantine/core'
import { H3, SortableTable } from '@packages/ui'
import {
  IconActivity,
  IconAlertTriangle,
  IconChartBar,
  IconUsers
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'

type ActivityLevel = 'vacant' | 'low' | 'moderate' | 'active'

type TenantActivitySummaryData = {
  success: boolean
  tenant_id: string
  generated_at: string
  content: {
    tasks: number
    pages: number
    chats: number
    messages: number
    notes: number
    events: number
    wallposts: number
    residents: number
  }
  content_recent: {
    tasks_30d: number
    pages_30d: number
    messages_30d: number
    wallposts_30d: number
  }
  users: {
    total: number
    active: number
    suspended: number
    never_signed_in: number
    signed_in_last_7d: number
    signed_in_last_30d: number
    last_tenant_sign_in_at: string | null
  }
  login_frequency: {
    sign_ins_last_7d: number
    sign_ins_last_30d: number
    sign_ins_last_90d: number
    avg_sign_ins_per_active_user_30d: number | null
  }
  users_detail: Array<{
    user_id: string
    full_name: string | null
    email: string | null
    type: string | null
    last_sign_in_at: string | null
    sign_ins_7d: number
    sign_ins_30d: number
  }>
  activity: {
    level: ActivityLevel
    label: string
    signals: string[]
  }
}

const activityBadgeColor: Record<ActivityLevel, string> = {
  active: 'green',
  moderate: 'yellow',
  low: 'orange',
  vacant: 'red'
}

const formatCount = (value: number | null | undefined) =>
  value == null ? '—' : value.toLocaleString()

const formatDate = (value: string | null | undefined) =>
  value ? dayjs(value).format('DD.MM.YYYY HH:mm') : 'Never'

export const TenantActivitySummary = ({ tenantId }: { tenantId: string }) => {
  const { supabase } = useSupabase()
  const [data, setData] = useState<TenantActivitySummaryData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadSummary = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data: response, error: invokeError } =
        await supabase.functions.invoke('admin-get-tenant-activity-summary', {
          body: { tenant_id: tenantId }
        })

      if (invokeError) {
        setError(invokeError.message)
        setData(null)
        return
      }

      if (!response?.success) {
        setError(response?.error ?? 'Failed to load activity summary')
        setData(null)
        return
      }

      setData(response as TenantActivitySummaryData)
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load activity summary')
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [supabase, tenantId])

  useEffect(() => {
    void loadSummary()
  }, [loadSummary])

  if (loading) {
    return (
      <Group gap="sm">
        <Loader size="sm" />
        <Text size="sm">Loading activity summary…</Text>
      </Group>
    )
  }

  if (error) {
    return (
      <Alert color="red" icon={<IconAlertTriangle />} title="Activity summary">
        {error}
      </Alert>
    )
  }

  if (!data) return null

  const contentRows = [
    { label: 'Tasks', total: data.content.tasks, recent: data.content_recent.tasks_30d },
    { label: 'Pages', total: data.content.pages, recent: data.content_recent.pages_30d },
    { label: 'Chats', total: data.content.chats, recent: null },
    { label: 'Messages', total: data.content.messages, recent: data.content_recent.messages_30d },
    { label: 'Notes', total: data.content.notes, recent: null },
    { label: 'Events', total: data.content.events, recent: null },
    { label: 'Wall posts', total: data.content.wallposts, recent: data.content_recent.wallposts_30d },
    { label: 'Residents', total: data.content.residents, recent: null }
  ]

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Group gap="xs">
          <IconActivity size={20} />
          <H3>Activity</H3>
        </Group>
        <Badge
          size="lg"
          color={activityBadgeColor[data.activity.level]}
          variant="filled"
        >
          {data.activity.label}
        </Badge>
      </Group>

      {data.activity.signals.length > 0 && (
        <Text size="sm" c="dimmed">
          {data.activity.signals.join(' · ')}
        </Text>
      )}

      <Grid className="text-sm">
        <Grid.Col span={6}>
          <Stack gap={4}>
            <Group gap={6}>
              <IconChartBar size={16} />
              <Text fw={600}>Content</Text>
            </Group>
            {contentRows.map((row) => (
              <Group key={row.label} justify="space-between" wrap="nowrap">
                <Text>{row.label}</Text>
                <Group gap="xs">
                  <Text fw={500}>{formatCount(row.total)}</Text>
                  {row.recent != null && (
                    <Text size="xs" c="dimmed">
                      (+{formatCount(row.recent)} / 30d)
                    </Text>
                  )}
                </Group>
              </Group>
            ))}
          </Stack>
        </Grid.Col>

        <Grid.Col span={6}>
          <Stack gap="md">
            <Stack gap={4}>
              <Group gap={6}>
                <IconUsers size={16} />
                <Text fw={600}>Users</Text>
              </Group>
              <Group justify="space-between">
                <Text>Total</Text>
                <Text fw={500}>{formatCount(data.users.total)}</Text>
              </Group>
              <Group justify="space-between">
                <Text>Active</Text>
                <Text fw={500}>{formatCount(data.users.active)}</Text>
              </Group>
              <Group justify="space-between">
                <Text>Signed in (7d / 30d)</Text>
                <Text fw={500}>
                  {formatCount(data.users.signed_in_last_7d)} /{' '}
                  {formatCount(data.users.signed_in_last_30d)}
                </Text>
              </Group>
              <Group justify="space-between">
                <Text>Never signed in</Text>
                <Text fw={500}>{formatCount(data.users.never_signed_in)}</Text>
              </Group>
              <Group justify="space-between">
                <Text>Last tenant login</Text>
                <Text fw={500}>
                  {formatDate(data.users.last_tenant_sign_in_at)}
                </Text>
              </Group>
            </Stack>

            <Stack gap={4}>
              <Text fw={600}>Login frequency</Text>
              <Group justify="space-between">
                <Text>Sign-ins (7d / 30d / 90d)</Text>
                <Text fw={500}>
                  {formatCount(data.login_frequency.sign_ins_last_7d)} /{' '}
                  {formatCount(data.login_frequency.sign_ins_last_30d)} /{' '}
                  {formatCount(data.login_frequency.sign_ins_last_90d)}
                </Text>
              </Group>
              <Group justify="space-between">
                <Text>Avg sign-ins per active user (30d)</Text>
                <Text fw={500}>
                  {data.login_frequency.avg_sign_ins_per_active_user_30d ?? '—'}
                </Text>
              </Group>
            </Stack>
          </Stack>
        </Grid.Col>
      </Grid>

      <Stack gap="xs">
        <Text fw={600}>User activity</Text>
        <SortableTable
          data={data.users_detail.map((user) => ({
            ...user,
            name: user.full_name || user.email || user.user_id,
            last_login: formatDate(user.last_sign_in_at),
            sign_ins: `${user.sign_ins_7d} / ${user.sign_ins_30d}`
          }))}
          columns={[
            { label: 'Name', key: 'name' },
            { label: 'Type', key: 'type' },
            { label: 'Last login', key: 'last_login' },
            { label: 'Sign-ins 7d / 30d', key: 'sign_ins' }
          ]}
        />
        <Text size="xs" c="dimmed">
          Generated {formatDate(data.generated_at)}
        </Text>
      </Stack>
    </Stack>
  )
}
